import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API } from "../../App";
import { CARIBBEAN_ISLANDS } from "../../lib/caribbeanIslands";
import { Clock, Crown, X } from "lucide-react";
import { Button } from "../ui/button";

export default function ProSearchHistory({ isPro, onApplySearch }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const islandNames = useMemo(
    () => Object.fromEntries(CARIBBEAN_ISLANDS.map((i) => [i.code, i.name])),
    []
  );

  const load = useCallback(async () => {
    if (!isPro) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/search-history`, { withCredentials: true });
      setHistory(res.data?.history || []);
    } catch {
      setHistory([]);
    } finally {
      setLoading(false);
    }
  }, [isPro]);

  useEffect(() => {
    load();
  }, [load]);

  const remove = async (historyId) => {
    try {
      await axios.delete(`${API}/search-history/${historyId}`, { withCredentials: true });
      setHistory((rows) => rows.filter((r) => r.history_id !== historyId));
    } catch (err) {
      console.warn("Delete search history failed", err);
    }
  };

  if (!isPro) {
    return (
      <div className="rounded-2xl border border-amber-100 bg-amber-50/80 p-4 text-sm text-amber-900">
        <p className="font-semibold flex items-center gap-2">
          <Crown className="w-4 h-4" /> PRO search memory
        </p>
        <p className="mt-1 text-amber-800/90">
          Upgrade to Explorer PRO to save island searches and reopen all-inclusive picks instantly.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <p className="text-sm font-semibold text-slate-800 flex items-center gap-2 mb-3">
        <Clock className="w-4 h-4 text-cyan-600" />
        Recent searches
      </p>
      {loading && <p className="text-xs text-slate-500">Loading…</p>}
      {!loading && history.length === 0 && (
        <p className="text-xs text-slate-500">Search a beach, restaurant, or tour — we&apos;ll remember it here.</p>
      )}
      <ul className="space-y-2">
        {history.map((entry) => (
          <li
            key={entry.history_id}
            className="flex items-center justify-between gap-2 rounded-xl bg-slate-50 px-3 py-2 text-sm"
          >
            <button
              type="button"
              className="text-left flex-1 hover:text-cyan-700"
              onClick={() =>
                onApplySearch?.({
                  search: entry.query || "",
                  country: entry.country || "barbados",
                  category: entry.category || "",
                  allInclusive: Boolean(entry.filters?.all_inclusive),
                })
              }
            >
              <span className="font-medium text-slate-800">{entry.query || "Browse all"}</span>
              <span className="block text-xs text-slate-500">
                {islandNames[entry.country] || entry.country}
                {entry.category ? ` · ${entry.category}` : ""}
                {entry.filters?.all_inclusive ? " · all-inclusive" : ""}
              </span>
            </button>
            <Button type="button" variant="ghost" size="icon" className="h-8 w-8" onClick={() => remove(entry.history_id)}>
              <X className="w-4 h-4" />
            </Button>
          </li>
        ))}
      </ul>
    </div>
  );
}

export async function saveProSearchHistory({ query, country, category, filters = {} }) {
  try {
    await axios.post(
      `${API}/search-history`,
      { query: query || "", country, category: category || null, filters },
      { withCredentials: true }
    );
  } catch {
    // Non-PRO or offline — ignore
  }
}
